import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, MapPin, CheckCircle, AlertTriangle, ExternalLink, Clock } from 'lucide-react';
import { formatDateMMDDYYYY } from '../utils/timeFormat';

/**
 * BreadcrumbViewer — opens a modal with:
 *   - venue coords + address
 *   - talent clock-in coords
 *   - distance + "at venue?" verdict
 *   - list of breadcrumb pings with timestamps
 *   - external map links (Google Maps)
 *
 * Usage:
 *   <BreadcrumbViewer bookingId={id} open={open} onOpenChange={setOpen} />
 */
const BreadcrumbViewer = ({ bookingId, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !bookingId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.get(`/gps/admin/breadcrumbs/${bookingId}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load tracking'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, bookingId]);

  const mapsLink = (loc) => loc ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;
  const dirLink = (from, to) => (from && to)
    ? `https://www.google.com/maps/dir/${from.lat},${from.lng}/${to.lat},${to.lng}`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="breadcrumb-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-cyan-600" /> Location verification
          </DialogTitle>
          <DialogDescription>
            Admin-only view of talent GPS at clock-in and during the shift.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-gray-400" data-testid="breadcrumb-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tracking…
          </div>
        )}

        {err && !loading && (
          <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="breadcrumb-error">
            {err}
          </div>
        )}

        {data && !loading && !err && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
              <div className="text-sm">
                <strong>{data.talent_name}</strong> · {data.venue_name}
              </div>
              <div className="text-xs text-gray-500">
                {data.venue_address} · {data.venue_city_state} · {data.shift_date ? formatDateMMDDYYYY(data.shift_date) : '—'}
              </div>
            </div>

            {/* Verdict */}
            {data.clock_in_location && data.venue_location ? (
              <div
                className={`rounded-lg border p-3 ${data.at_venue ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
                data-testid="breadcrumb-verdict"
              >
                <div className="flex items-start gap-2">
                  {data.at_venue
                    ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                  <div className="text-sm">
                    <div className={`font-semibold ${data.at_venue ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {data.at_venue ? 'Clocked in at venue' : 'Clocked in off-site'}
                    </div>
                    <div className="text-xs text-gray-700 mt-0.5">
                      Distance from venue: <strong>{data.distance_from_venue_meters} m</strong>{' '}
                      {data.at_venue ? '(within 200 m tolerance)' : '(more than 200 m from address on file)'}
                    </div>
                    {data.clock_in_time && (
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Clock-in: {new Date(data.clock_in_time).toLocaleString('en-US')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500" data-testid="breadcrumb-no-coords">
                No clock-in GPS recorded{data.venue_location ? '.' : ' (and no venue coords either).'}
              </div>
            )}

            {/* Action links */}
            <div className="flex flex-wrap gap-2">
              {data.venue_location && (
                <Button size="sm" variant="outline" asChild>
                  <a href={mapsLink(data.venue_location)} target="_blank" rel="noopener noreferrer" data-testid="link-venue-map">
                    <MapPin className="w-3.5 h-3.5 mr-1" /> Venue on map <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </Button>
              )}
              {data.clock_in_location && (
                <Button size="sm" variant="outline" asChild>
                  <a href={mapsLink(data.clock_in_location)} target="_blank" rel="noopener noreferrer" data-testid="link-clockin-map">
                    <MapPin className="w-3.5 h-3.5 mr-1" /> Clock-in on map <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </Button>
              )}
              {data.venue_location && data.clock_in_location && (
                <Button size="sm" variant="outline" asChild>
                  <a href={dirLink(data.venue_location, data.clock_in_location)} target="_blank" rel="noopener noreferrer" data-testid="link-directions">
                    Directions between points <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </Button>
              )}
            </div>

            {/* Breadcrumb list */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">GPS breadcrumbs</div>
                <Badge variant="outline">{data.total_points} points</Badge>
              </div>
              {data.total_points === 0 ? (
                <div className="text-xs text-gray-500">No background tracking recorded for this booking.</div>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded border border-gray-100 divide-y divide-gray-100" data-testid="breadcrumb-list">
                  {data.breadcrumbs.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                      <span className="text-gray-500 font-mono">
                        {b.recorded_at ? new Date(b.recorded_at).toLocaleTimeString('en-US') : '—'}
                      </span>
                      <a
                        href={`https://www.google.com/maps?q=${b.latitude},${b.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-700 hover:underline truncate"
                      >
                        {b.latitude.toFixed(5)}, {b.longitude.toFixed(5)}
                      </a>
                      {b.accuracy_meters != null && (
                        <span className="text-gray-400 shrink-0">±{Math.round(b.accuracy_meters)}m</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BreadcrumbViewer;
